/**
 * Lightweight Subject implementation
 * Replaces rxjs Subject to save ~210KB bundle size
 *
 * API compatible with rxjs Subject for the methods we use:
 * - subscribe(callback)
 * - next(value)
 * - complete()
 */

type Observer<T> = (value: T) => void;
type Unsubscribe = () => void;

export class Subject<T> {
  private observers: Set<Observer<T>> = new Set();
  private isCompleted = false;

  /**
   * Subscribe to receive values
   */
  subscribe(observer: Observer<T>): { unsubscribe: Unsubscribe } {
    if (this.isCompleted) {
      return { unsubscribe: () => {} };
    }

    this.observers.add(observer);

    return {
      unsubscribe: () => {
        this.observers.delete(observer);
      }
    };
  }

  /**
   * Emit a value to all subscribers
   */
  next(value: T): void {
    if (this.isCompleted) return;

    this.observers.forEach(observer => {
      try {
        observer(value);
      } catch (error) {
        console.error('[Subject] Error in observer:', error);
      }
    });
  }

  /**
   * Complete the subject, preventing further emissions
   */
  complete(): void {
    this.isCompleted = true;
    this.observers.clear();
  }

  /**
   * Get the number of active subscribers
   */
  get observerCount(): number {
    return this.observers.size;
  }
}

/**
 * Lightweight BehaviorSubject implementation
 * Holds a current value and emits it to new subscribers immediately
 */
export class BehaviorSubject<T> extends Subject<T> {
  private currentValue: T;

  constructor(initialValue: T) {
    super();
    this.currentValue = initialValue;
  }

  /**
   * Get the current value
   */
  get value(): T {
    return this.currentValue;
  }

  /**
   * Subscribe and immediately receive the current value
   */
  override subscribe(observer: Observer<T>): { unsubscribe: Unsubscribe } {
    // Emit current value immediately to new subscriber
    try {
      observer(this.currentValue);
    } catch (error) {
      console.error('[BehaviorSubject] Error in observer:', error);
    }

    return super.subscribe(observer);
  }

  /**
   * Emit a new value and store it as current
   */
  override next(value: T): void {
    this.currentValue = value;
    super.next(value);
  }
}
